import React from "react";
import { Link } from "react-router-dom";
import { Flame, Zap, Package, HeartPulse, ArrowRight } from "lucide-react";

const CATEGORY_TEASERS = [
  {
    id: "gases",
    icon: Flame,
    name: "Gases",
    desc: "Oxígeno, nitrógeno, argón, CO₂, helio, acetileno y mezclas industriales.",
    accent: "red",
  },
  {
    id: "soldadura",
    icon: Zap,
    name: "Soldadura",
    desc: "Antorchas MIG/TIG, sopletes, reguladores y todo lo necesario.",
    accent: "orange",
  },
  {
    id: "consumibles",
    icon: Package,
    name: "Consumibles",
    desc: "Boquillas, toberas, electrodos, alambre, discos abrasivos y conexiones.",
    accent: "navy",
  },
  {
    id: "medicinal",
    icon: HeartPulse,
    name: "Medicinal",
    desc: "Oxígeno medicinal, tanques portátiles, reguladores y recarga a domicilio.",
    accent: "red",
  },
];

const ACCENT_BG = {
  red: "bg-gradient-to-br from-[#D90429] via-[#B0001E] to-[#0F172A]",
  orange: "bg-gradient-to-br from-[#EA580C] via-[#C2410C] to-[#0F172A]",
  navy: "bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]",
};

export default function Services() {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="py-20 lg:py-28 bg-[var(--cima-surface)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="divider-orange mb-5" />
            <p className="font-mono-cima text-xs uppercase tracking-widest text-[var(--cima-red)] mb-3">
              Catálogo CIMA
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight text-[var(--cima-navy)]">
              Gases, soldadura, consumibles y medicinal.
            </h2>
            <p className="mt-4 text-slate-600">
              Una sola fuente confiable para toda tu operación — explora nuestro catálogo
              completo o consulta directamente con nosotros.
            </p>
          </div>

          <Link
            to="/catalogo"
            data-testid="services-view-catalog-top"
            className="btn-cima-red px-6 py-3.5 text-sm font-semibold uppercase tracking-wide inline-flex items-center justify-center gap-2 self-start lg:self-end"
          >
            Ver Catálogo Completo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* 4 category cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {CATEGORY_TEASERS.map((c, i) => (
            <Link
              key={c.id}
              to={`/catalogo#${c.id}`}
              data-testid={`services-category-${c.id}`}
              className="sharp-card group flex flex-col overflow-hidden"
            >
              <div className={`relative aspect-[4/3] ${ACCENT_BG[c.accent]} grain-overlay flex items-center justify-center overflow-hidden`}>
                <c.icon
                  className="w-20 h-20 text-white/95 group-hover:scale-110 transition-transform duration-500"
                  strokeWidth={1.3}
                />
                <div className="absolute top-3 left-3 bg-white/95 px-2.5 py-1 text-[10px] font-mono-cima uppercase tracking-widest text-slate-700">
                  Categoría
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 stripe-accent opacity-40 rotate-12" />
              </div>
              <div className="p-5 flex flex-col flex-1 bg-white">
                <h3 className="font-display font-bold text-xl text-[var(--cima-navy)] leading-tight">
                  {c.name}
                </h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-5 inline-flex items-center justify-between gap-2 text-xs font-mono-cima uppercase tracking-widest text-slate-500 group-hover:text-[var(--cima-red)] transition-colors">
                  <span>Ver productos</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-12 border border-slate-300 bg-white p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <div className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[var(--cima-red)] mb-1">
              + Productos disponibles
            </div>
            <div className="font-display font-bold text-xl lg:text-2xl text-[var(--cima-navy)] leading-tight">
              Explora más de 30 productos en nuestro catálogo completo.
            </div>
          </div>
          <Link
            to="/catalogo"
            data-testid="services-view-catalog-bottom"
            className="btn-cima-red px-7 py-3.5 text-sm font-semibold uppercase tracking-wide inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            Ver Catálogo Completo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
